/**
 * Question management component for admin panel.
 *
 * Exam question management interface with comprehensive CRUD operations,
 * filtering, pagination, batch creation, and preview capabilities.
 *
 * Route: `/admin/questions`
 * Requires authentication: Yes (admin only)
 *
 * @module QuestionManager
 */

import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Plus,
  Edit,
  Trash2,
  Search,
  Eye,
  FileText,
  BookOpen,
  RefreshCw,
  AlertCircle,
  Image,
  Loader2,
  FileArchive,
} from 'lucide-react';
import { toast } from 'sonner';
import QuestionForm from '@/components/admin/QuestionForm';
import type { Context as ApiContext } from '@/lib/contextApi';
import QuestionPreview from '@/components/admin/QuestionPreview';
import {
  getAllQuestions,
  getQuestionById,
  createQuestion,
  updateQuestion,
  deleteQuestion,
  deleteBulkQuestions,
  type QuestionFilters,
} from '@/lib/questionApi';
import { logger } from '@/lib/logger';
import { parseQuestionOptions, type OptionValue } from '@/lib/questionOptions';
import { type QuestionTypeValue } from '@/components/admin/questionForm/constants';
import { QuestionSetExtractionDialog } from '@/components/admin/QuestionSetExtractionDialog';

interface Context {
  id: number;
  name: string;
  type: 'text' | 'image' | 'audio';
  content: string;
  fileExtension?: string;
  mimeType?: string;
  binaryContent?: string;
  mediaUrl?: string | null;
  generationModel?: string | null;
}

interface Question {
  id: string;
  text: string;
  type: QuestionTypeValue;
  skill: string;
  topic?: string;
  level: 'A1' | 'A2' | 'B1' | 'B2' | 'C1' | 'C2';
  isActive: boolean;
  createdAt: string;
  createdBy?: string;
  options?: OptionValue[];
  expectedAnswer: string | string[];
  explanation: string;
  taskType?: string | null;
  materialUrl?: string;
  materialType?: 'image' | 'audio' | 'video';
  section: number;
  examTypes: string[];
  context?: Context;
  generationModel?: string | null;
}

const transformApiQuestionToQuestion = (apiQuestion: any): Question => {
  return {
    id: apiQuestion.id,
    text: apiQuestion.text || '',
    type: apiQuestion.type as Question['type'],
    skill: apiQuestion.skill || '',
    level: apiQuestion.level as Question['level'],
    isActive: apiQuestion.isActive ?? true,
    createdAt: apiQuestion.createdAt,
    options: parseQuestionOptions(apiQuestion.options),
    expectedAnswer: apiQuestion.expectedAnswer || '',
    explanation: apiQuestion.explanation || '',
    taskType: apiQuestion.taskType || null,
    section: apiQuestion.section,
    examTypes: Array.isArray(apiQuestion.examTypes) ? apiQuestion.examTypes : [],
    context: apiQuestion.context,
    generationModel: apiQuestion.generationModel ?? null,
  };
};

const adaptQuestionForForm = (q: Question | null): any => {
  if (!q) return null;
  const adapted: any = { ...q };
  if (q.context) {
    const ctx = q.context as any;
    const apiCtx: ApiContext = {
      id: ctx.id,
      name: ctx.name,
      type: ctx.type,
      content: ctx.content,
      fileExtension: ctx.fileExtension,
      mimeType: ctx.mimeType,
      binaryContent: ctx.binaryContent,
      mediaUrl: ctx.mediaUrl,
      transcript: ctx.transcript || undefined,
      createdAt: ctx.createdAt || '',
      updatedAt: ctx.updatedAt || '',
      textPreview: ctx.textPreview,
      binarySize: ctx.binarySize,
      generationModel: ctx.generationModel ?? null,
      hasFile:
        typeof ctx.hasFile === 'boolean' ? ctx.hasFile : !!ctx.binaryContent,
    };
    adapted.context = apiCtx;
  }
  return adapted;
};

export default function QuestionManager() {
  const [questions, setQuestions] = useState<Question[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pagination, setPagination] = useState({
    page: 1,
    totalPages: 1,
    total: 0,
  });
  const [selectedQuestions, setSelectedQuestions] = useState<string[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterLevel, setFilterLevel] = useState<string>('all');
  const [filterSection, setFilterSection] = useState<string>('all');
  const [filterExamType, setFilterExamType] = useState<string>('all');
  const [filterModule, setFilterModule] = useState<string>('all');
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [showQuestionForm, setShowQuestionForm] = useState(false);
  const [editingQuestion, setEditingQuestion] = useState<Question | null>(null);
  const [previewQuestion, setPreviewQuestion] = useState<Question | null>(null);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showBulkDeleteDialog, setShowBulkDeleteDialog] = useState(false);
  const [questionToDelete, setQuestionToDelete] = useState<Question | null>(
    null,
  );
  const [deleting, setDeleting] = useState(false);
  const [showExtractionDialog, setShowExtractionDialog] = useState(false);

  const levels = ['A1', 'A2', 'B1', 'B2', 'C1', 'C2'];
  const sections = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10];
  const examTypes = [
    { value: 'goethe', label: 'Goethe-Zertifikat' },
    { value: 'ösd', label: 'ÖSD Zertifikat' },
    { value: 'telc', label: 'telc Deutsch' },
  ];
  const modules = [
    { value: 'reading', label: 'Reading' },
    { value: 'listening', label: 'Listening' },
    { value: 'writing', label: 'Writing' },
    { value: 'speaking', label: 'Speaking' },
  ];

  const loadQuestions = async () => {
    setLoading(true);
    setError(null);

    try {
      const filters: QuestionFilters = {
        level: filterLevel === 'all' ? undefined : filterLevel,
        section: filterSection === 'all' ? undefined : filterSection,
        examType: filterExamType === 'all' ? undefined : filterExamType,
        module: filterModule === 'all' ? undefined : filterModule,
        search: searchTerm || undefined,
        isActive:
          filterStatus === 'all'
            ? undefined
            : filterStatus === 'active',
      };

      const response = await getAllQuestions(filters, {
        page: pagination.page,
        limit: 10,
      });

      setQuestions(response.items.map(transformApiQuestionToQuestion));
      setPagination({
        page: response.page,
        totalPages: response.totalPages,
        total: response.total,
      });
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : 'Failed to load questions';
      setError(errorMessage);
      toast.error('Failed to load questions');
      logger.error('Error loading questions', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadQuestions();
  }, [
    filterLevel,
    filterSection,
    filterExamType,
    filterModule,
    filterStatus,
    searchTerm,
    pagination.page,
  ]);

  useEffect(() => {
    if (pagination.page !== 1) {
      setPagination((prev) => ({ ...prev, page: 1 }));
    }
  }, [filterLevel, filterSection, filterExamType, filterModule, filterStatus, searchTerm]);

  const handleCreateQuestion = async (
    questionData: Partial<Question> & {
      count?: number;
      createdQuestions?: unknown[];
    },
  ) => {
    try {
      setLoading(true);

      if (questionData.count && questionData.createdQuestions) {
        setShowQuestionForm(false);
        toast.success(
          `Successfully created ${questionData.count} questions!`,
        );
      } else {
        await createQuestion(questionData);
        setShowQuestionForm(false);
        toast.success('Question created successfully');
      }

      loadQuestions();
    } catch (error) {
      toast.error('Failed to create question');
      logger.error('Create question error', error);
    } finally {
      setLoading(false);
    }
  };

  const handleEditQuestion = async (questionData: Partial<Question>) => {
    if (!editingQuestion) return;

    try {
      setLoading(true);
      await updateQuestion(editingQuestion.id, questionData);
      setEditingQuestion(null);
      toast.success('Question updated successfully');
      loadQuestions();
    } catch (error) {
      toast.error('Failed to update question');
      logger.error('Update question error', error);
    } finally {
      setLoading(false);
    }
  };

  const openDeleteDialog = (question: Question) => {
    setQuestionToDelete(question);
    setShowDeleteDialog(true);
  };

  const handleDeleteQuestion = async () => {
    if (!questionToDelete) return;

    try {
      setDeleting(true);
      await deleteQuestion(questionToDelete.id);
      toast.success('Question deleted successfully');
      setShowDeleteDialog(false);
      setQuestionToDelete(null);
      loadQuestions();
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : 'Failed to delete question';
      toast.error(message);
      logger.error('Delete question error', error);
    } finally {
      setDeleting(false);
    }
  };

  const openBulkDeleteDialog = () => {
    if (selectedQuestions.length === 0) return;
    setShowBulkDeleteDialog(true);
  };

  const handleBulkDelete = async () => {
    if (selectedQuestions.length === 0) return;

    try {
      setDeleting(true);
      await deleteBulkQuestions(selectedQuestions);
      const count = selectedQuestions.length;
      setSelectedQuestions([]);
      setShowBulkDeleteDialog(false);
      toast.success(`Successfully deleted ${count} question${count === 1 ? '' : 's'}`);
      loadQuestions();
    } catch (error) {
      const message =
        error instanceof Error ? error.message : 'Failed to delete questions';
      toast.error(message);
      logger.error('Bulk delete error', error);
    } finally {
      setDeleting(false);
    }
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'multiple-choice-single':
      case 'multiple-choice-multi':
      case 'true-false':
      case 'text-short':
        return <FileText className="h-4 w-4" />;
      case 'text-long':
        return <BookOpen className="h-4 w-4" />;
      default:
        return <FileText className="h-4 w-4" />;
    }
  };

  const getTypeLabel = (type: string) => {
    switch (type) {
      case 'multiple-choice-single':
        return 'MC Single';
      case 'multiple-choice-multi':
        return 'MC Multi';
      case 'true-false':
        return 'True/False';
      case 'text-short':
        return 'Short Text';
      case 'text-long':
        return 'Long Text';
      default:
        return type;
    }
  };

  return (
    <div className="space-y-6">
      {/* Question Form Modal */}
      <Dialog
        open={!!(showQuestionForm || editingQuestion)}
        onOpenChange={(open) => {
          if (!open) {
            setShowQuestionForm(false);
            setEditingQuestion(null);
          }
        }}
      >
        <DialogContent
          className="max-w-6xl max-h-[90vh] overflow-y-auto"
          onOpenAutoFocus={(e) => e.preventDefault()}
        >
          <DialogHeader>
            <DialogTitle className="font-ui">
              {editingQuestion ? 'Edit Question' : 'Create New Question'}
            </DialogTitle>
            <DialogDescription className="font-ui">
              {editingQuestion
                ? 'Update question details and settings'
                : 'Create a new exam question with context and parameters'}
            </DialogDescription>
          </DialogHeader>
          <QuestionForm
            question={adaptQuestionForForm(editingQuestion)}
            onSave={(q) => {
              if (editingQuestion) {
                void handleEditQuestion(q as any);
              } else {
                void handleCreateQuestion(q as any);
              }
            }}
            onCancel={() => {
              setShowQuestionForm(false);
              setEditingQuestion(null);
            }}
          />
        </DialogContent>
      </Dialog>

      <QuestionSetExtractionDialog
        open={showExtractionDialog}
        onOpenChange={setShowExtractionDialog}
      />

      {/* Question Management Panel */}
      <Card>
        <CardHeader>
          <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
            <div>
              <CardTitle className="font-ui text-2xl flex items-center gap-2">
                Question Management
                {loading && <Loader2 className="h-4 w-4 animate-spin" />}
              </CardTitle>
              <CardDescription className="font-ui">
                Manage exam questions with filtering, search, and CRUD operations
              </CardDescription>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <Button
                variant="outline"
                onClick={() => setShowExtractionDialog(true)}
                disabled={loading}
                className="flex items-center gap-2"
              >
                <FileArchive className="h-4 w-4" />
                <span className="font-ui">Extract question set</span>
              </Button>
              <Button
                variant="outline"
                onClick={loadQuestions}
                disabled={loading}
                className="flex items-center gap-2"
              >
                <RefreshCw
                  className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`}
                />
                <span className="font-ui">Refresh</span>
              </Button>
              <Button
                onClick={() => setShowQuestionForm(true)}
                className="flex items-center gap-2"
                disabled={loading}
              >
                <Plus className="h-4 w-4" />
                <span className="font-ui">Add Question</span>
              </Button>
            </div>
          </div>
          {error && (
            <div className="mt-4 p-3 bg-destructive/10 border border-destructive/20 rounded-lg flex items-center gap-2">
              <AlertCircle className="h-4 w-4 text-destructive" />
              <span className="font-ui text-sm text-destructive">{error}</span>
            </div>
          )}
        </CardHeader>

        <CardContent className="space-y-6">
          {/* Search and Filters */}
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search questions..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9"
              />
            </div>

            <Select value={filterExamType} onValueChange={setFilterExamType}>
              <SelectTrigger className="w-48">
                <SelectValue placeholder="Exam type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Exam Types</SelectItem>
                {examTypes.map((examType) => (
                  <SelectItem key={examType.value} value={examType.value}>
                    {examType.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={filterLevel} onValueChange={setFilterLevel}>
              <SelectTrigger className="w-32">
                <SelectValue placeholder="Level" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Levels</SelectItem>
                {levels.map((level) => (
                  <SelectItem key={level} value={level}>
                    {level}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={filterModule} onValueChange={setFilterModule}>
              <SelectTrigger className="w-40">
                <SelectValue placeholder="Module" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Modules</SelectItem>
                {modules.map((module) => (
                  <SelectItem key={module.value} value={module.value}>
                    {module.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={filterSection} onValueChange={setFilterSection}>
              <SelectTrigger className="w-36">
                <SelectValue placeholder="Section" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Sections</SelectItem>
                {sections.map((section) => (
                  <SelectItem key={section} value={section.toString()}>
                    Section {section}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger className="w-32">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="inactive">Inactive</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Bulk Actions */}
          {selectedQuestions.length > 0 && (
            <div className="flex items-center gap-4 p-4 bg-muted/50 rounded-lg">
              <span className="font-ui text-sm font-medium">
                {selectedQuestions.length} question
                {selectedQuestions.length === 1 ? '' : 's'} selected
              </span>
              <Button
                variant="destructive"
                size="sm"
                onClick={openBulkDeleteDialog}
                className="flex items-center gap-2"
              >
                <Trash2 className="h-4 w-4" />
                <span className="font-ui">Delete Selected</span>
              </Button>
            </div>
          )}

          {/* Questions Table */}
          <div className="border rounded-lg overflow-hidden">
            <div className="overflow-x-auto">
              <Table className="table-fixed min-w-full">
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-12">
                      <Checkbox
                        checked={
                          selectedQuestions.length === questions.length &&
                          questions.length > 0
                        }
                        onCheckedChange={(checked) => {
                          if (checked) {
                            setSelectedQuestions(questions.map((q) => q.id));
                          } else {
                            setSelectedQuestions([]);
                          }
                        }}
                      />
                    </TableHead>
                    <TableHead className="w-96 font-ui">Question</TableHead>
                    <TableHead className="w-32 font-ui">Type</TableHead>
                    <TableHead className="w-16 font-ui">Level</TableHead>
                    <TableHead className="w-24 font-ui">Exam Types</TableHead>
                    <TableHead className="w-20 font-ui">Skill</TableHead>
                    <TableHead className="w-24 font-ui">Section</TableHead>
                    <TableHead className="w-20 font-ui">Status</TableHead>
                    <TableHead className="w-24 font-ui">Created</TableHead>
                    <TableHead className="w-20 font-ui">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {loading && questions.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={11} className="text-center py-8">
                        <div className="flex items-center justify-center gap-2">
                          <Loader2 className="h-4 w-4 animate-spin" />
                          <span className="font-ui">Loading...</span>
                        </div>
                      </TableCell>
                    </TableRow>
                  )}
                  {!loading && questions.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={11} className="text-center py-8">
                        <div className="flex flex-col items-center gap-2 text-muted-foreground">
                          <FileText className="h-8 w-8" />
                          <p className="font-ui text-lg font-medium">
                            No questions found
                          </p>
                          <p className="font-ui text-sm">
                            Try adjusting your filters or create a new question
                          </p>
                        </div>
                      </TableCell>
                    </TableRow>
                  )}
                  {!loading && questions.length > 0 && (
                    questions.map((question) => (
                      <TableRow key={question.id}>
                        <TableCell>
                          <Checkbox
                            checked={selectedQuestions.includes(question.id)}
                            onCheckedChange={(checked) => {
                              if (checked) {
                                setSelectedQuestions([
                                  ...selectedQuestions,
                                  question.id,
                                ]);
                              } else {
                                setSelectedQuestions(
                                  selectedQuestions.filter(
                                    (id) => id !== question.id,
                                  ),
                                );
                              }
                            }}
                          />
                        </TableCell>
                        <TableCell className="w-96 max-w-96">
                          <div className="flex items-start gap-2 min-w-0">
                            {getTypeIcon(question.type)}
                            <div className="min-w-0 flex-1">
                              <p className="font-ui font-medium truncate text-sm leading-tight">
                                {question.text}
                              </p>
                              {question.materialUrl && (
                                <div className="flex items-center gap-1 mt-1">
                                  <Image className="h-3 w-3 text-muted-foreground" />
                                  <span className="font-ui text-xs text-muted-foreground">
                                    Has material
                                  </span>
                                </div>
                              )}
                              {question.generationModel && (
                                <div className="mt-1">
                                  <Badge variant="outline" className="font-ui text-[10px]">
                                    Model: {question.generationModel}
                                  </Badge>
                                </div>
                              )}
                            </div>
                          </div>
                        </TableCell>
                        <TableCell className="w-32">
                          <Badge
                            variant="outline"
                            className="text-xs whitespace-nowrap"
                          >
                            <span className="font-ui">{getTypeLabel(question.type)}</span>
                          </Badge>
                        </TableCell>
                        <TableCell className="w-16">
                          <Badge variant="secondary" className="text-xs">
                            <span className="font-ui">{question.level}</span>
                          </Badge>
                        </TableCell>
                        <TableCell className="w-24">
                          <div className="flex flex-col gap-1">
                            {question.examTypes.slice(0, 2).map((examType) => (
                              <Badge
                                key={examType}
                                variant="secondary"
                                className="text-xs whitespace-nowrap"
                              >
                                <span className="font-ui">
                                  {examType.toUpperCase()}
                                </span>
                              </Badge>
                            ))}
                            {question.examTypes.length > 2 && (
                              <span className="font-ui text-xs text-muted-foreground">
                                +{question.examTypes.length - 2}
                              </span>
                            )}
                          </div>
                        </TableCell>
                        <TableCell className="w-20 font-ui text-sm">
                          {question.skill}
                        </TableCell>
                        <TableCell className="w-24">
                          <Badge
                            variant="outline"
                            className="text-xs whitespace-nowrap"
                          >
                            <span className="font-ui">Sec {question.section}</span>
                          </Badge>
                        </TableCell>
                        <TableCell className="w-20">
                          <Badge
                            variant={
                              question.isActive ? 'secondary' : 'outline'
                            }
                            className="text-xs whitespace-nowrap"
                          >
                            <span className="font-ui">
                              {question.isActive ? 'Active' : 'Inactive'}
                            </span>
                          </Badge>
                        </TableCell>
                        <TableCell className="w-24 font-ui text-xs text-muted-foreground">
                          {question.createdAt}
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1">
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => setPreviewQuestion(question)}
                              title="Preview"
                            >
                              <Eye className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={async () => {
                                try {
                                  setLoading(true);
                                  const freshQuestion = await getQuestionById(
                                    question.id,
                                  );
                                  setEditingQuestion(freshQuestion);
                                } catch (error) {
                                  toast.error(
                                    'Failed to load question for editing',
                                  );
                                  logger.error('Error loading question', error);
                                } finally {
                                  setLoading(false);
                                }
                              }}
                              title="Edit"
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => openDeleteDialog(question)}
                              title="Delete"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          </div>

          {/* Pagination */}
          <div className="flex items-center justify-between">
            <p className="font-ui text-sm text-muted-foreground">
              Showing {questions.length} of {pagination.total} questions (Page{' '}
              {pagination.page} of {pagination.totalPages})
            </p>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() =>
                  setPagination((prev) => ({ ...prev, page: prev.page - 1 }))
                }
                disabled={pagination.page <= 1 || loading}
              >
                <span className="font-ui">Previous</span>
              </Button>
              <span className="font-ui text-sm text-muted-foreground px-2">
                {pagination.page} of {pagination.totalPages}
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={() =>
                  setPagination((prev) => ({ ...prev, page: prev.page + 1 }))
                }
                disabled={pagination.page >= pagination.totalPages || loading}
              >
                <span className="font-ui">Next</span>
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Question Preview Modal */}
      <Dialog
        open={!!previewQuestion}
        onOpenChange={(open) => !open && setPreviewQuestion(null)}
      >
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-ui">Question Preview</DialogTitle>
            <DialogDescription className="font-ui">
              Preview how this question will appear to students
            </DialogDescription>
          </DialogHeader>
          {previewQuestion && (
            <QuestionPreview
              question={previewQuestion}
              onClose={() => setPreviewQuestion(null)}
              onEdit={async () => {
                setPreviewQuestion(null);
                try {
                  setLoading(true);
                  const freshQuestion = await getQuestionById(
                    previewQuestion.id,
                  );
                  setEditingQuestion(freshQuestion);
                } catch (error) {
                  toast.error('Failed to load question for editing');
                  logger.error('Error loading question', error);
                } finally {
                  setLoading(false);
                }
              }}
              onViewRelated={async (relatedQuestion: Question) => {
                try {
                  setLoading(true);
                  const fullQuestion = await getQuestionById(
                    relatedQuestion.id,
                  );
                  setPreviewQuestion(transformApiQuestionToQuestion(fullQuestion));
                } catch (error) {
                  toast.error('Failed to load related question');
                  logger.error('Error loading related question', error);
                } finally {
                  setLoading(false);
                }
              }}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="font-ui">Delete Question</AlertDialogTitle>
            <AlertDialogDescription className="font-ui">
              {questionToDelete &&
                `Are you sure you want to delete this question? This action cannot be undone.${
                  questionToDelete.text.length > 100
                    ? `\n\n"${questionToDelete.text.substring(0, 100)}..."`
                    : `\n\n"${questionToDelete.text}"`
                }`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>
              <span className="font-ui">Cancel</span>
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteQuestion}
              disabled={deleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              <span className="font-ui">
                {deleting ? 'Deleting...' : 'Delete'}
              </span>
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Bulk Delete Confirmation Dialog */}
      <AlertDialog
        open={showBulkDeleteDialog}
        onOpenChange={setShowBulkDeleteDialog}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="font-ui">
              Delete Multiple Questions
            </AlertDialogTitle>
            <AlertDialogDescription className="font-ui">
              Are you sure you want to delete {selectedQuestions.length} question
              {selectedQuestions.length === 1 ? '' : 's'}? This action cannot be
              undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>
              <span className="font-ui">Cancel</span>
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleBulkDelete}
              disabled={deleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              <span className="font-ui">
                {deleting ? 'Deleting...' : 'Delete'}
              </span>
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
